import React, {useMemo} from 'react';
import {Icon, Text} from '../../components';
import {colors} from '../../theme';
import {thinMargins} from '../../constants';
import {useScreenSize, usePageWidth, useCarouselNavigation} from '../../utils';

export interface IStatCard {
  emoji: string;
  date?: string;
  stat: string;
  caption: string;
  variant?: 'about' | 'work';
}

export interface IStats {
  stats: IStatCard[];
  variant?: 'about' | 'work';
  title: string;
}

const StatCard = ({emoji, date, stat, caption, variant}: IStatCard) => {
  return (
    <div
      className={`rounded-[2rem] ${
        variant === 'work' ? 'bg-bluerice' : 'bg-sandwisp'
      } flex flex-col justify-between text-center items-center h-full px-5 lg:px-8 py-12 lg:py-16 w-[18rem] min-w-[18rem] lg:w-[26rem] lg:min-w-[26rem]`}>
      <div>
        <p className="text-h2mob lg:text-h2 pb-6">{emoji}</p>
        {date && (
          <Text as="p" variant="LargeBodyBold">
            {date}
          </Text>
        )}
      </div>
      <Text as="h3" variant="Subtitle1">
        {stat}
      </Text>
      <Text as="p" variant="LargeBody">
        {caption}
      </Text>
    </div>
  );
};

export const StatCardCarousel = ({stats, variant = 'about', title}: IStats) => {
  const screenSize = useScreenSize();

  const pageWidth = usePageWidth();

  const breakpointConfig = useMemo((): {
    remPerItem: number;
    lastSlideIndex: number;
    marginLeft: number;
  } => {
    // setting different "lastSlideIndex" helps minimise extra white space at different breakpoints
    if (screenSize === 'xx-large') {
      return {
        remPerItem: 27.25,
        lastSlideIndex: stats.length - 2,
        marginLeft: (pageWidth - 1280) / 2,
      };
    } else if (screenSize === 'extra-large') {
      return {
        remPerItem: 27.25,
        lastSlideIndex: stats.length - 1,
        marginLeft: 160,
      };
    } else if (screenSize === 'large') {
      return {
        remPerItem: 27.25,
        lastSlideIndex: stats.length - 1,
        marginLeft: 128,
      };
    } else if (screenSize === 'medium') {
      return {
        remPerItem: 27.25,
        lastSlideIndex: stats.length - 2,
        marginLeft: 32,
      };
    } else {
      return {
        remPerItem: 19.25,
        lastSlideIndex: stats.length,
        marginLeft: 32,
      };
    }
  }, [screenSize, pageWidth, stats.length]);

  const {
    currentSlideIndex,
    onGoToPreviousSlide,
    onGoToNextSlide,
    isAtStart,
    isAtEnd,
    swipeHandlers,
  } = useCarouselNavigation({
    itemCount: breakpointConfig.lastSlideIndex,
    wrap: false,
  });

  const transformSize = `translatex(-${
    currentSlideIndex * breakpointConfig.remPerItem
  }rem)`;

  return (
    <div className="relative text-black ">
      <div className={`max-w-screen-xl  ${thinMargins} pb-10`}>
        <Text as="h3" variant="Title">
          {title}
        </Text>
      </div>
      <div
        {...swipeHandlers}
        style={{marginLeft: `${breakpointConfig.marginLeft}px`}}>
        <div className="overflow-hidden">
          <div
            className={`flex transition ease-out duration-500 gap-5 h-[26rem] lg:h-[32rem]`}
            style={{
              transform: transformSize,
            }}>
            {stats.map((m, i) => (
              <StatCard
                emoji={m.emoji}
                date={m.date}
                stat={m.stat}
                caption={m.caption}
                key={i}
                variant={variant}
              />
            ))}
          </div>
        </div>
      </div>
      <div
        className={`absolute top-[55%] w-full hidden lg:flex lg:justify-between `}
        style={{
          paddingLeft: `${breakpointConfig.marginLeft - 100}px`,
          paddingRight: `${breakpointConfig.marginLeft - 100}px`,
        }}>
        <button
          onClick={onGoToPreviousSlide}
          disabled={isAtStart}
          className="disabled:opacity-70">
          <Icon iconName={'left'} fill={colors.white} height={75} width={75} />
        </button>
        <button
          onClick={onGoToNextSlide}
          disabled={isAtEnd}
          className="disabled:opacity-70">
          <Icon iconName={'right'} fill={colors.white} height={75} width={75} />
        </button>
      </div>
    </div>
  );
};
